import { Box, TypographyStylesProvider } from "@mantine/core";
import React from "react";
import { IconCircleMinus } from "@tabler/icons-react";
import DocsButton from "../articlesHelpers/DocsButton";
import { JsonView, allExpanded, darkStyles } from "react-json-view-lite";

export const CLOSED_CHANNELS_CONTENT = (
  <TypographyStylesProvider>
    <p>
      Once you close a channel, the channel goes into a CLOSING state. In the
      case of a cooperative close, where both nodes are online, the closure
      should complete within a few minutes and your balance in the channel will
      be quickly added to your BTC Balance.
    </p>

    <p>
      One tricky thing about cooperative closes is that the two sides of the
      channel need to "negotiate" the fees that will be paid for the
      channel-closure transaction. Things can sometimes go bad here, especially
      when the channels are between two different node implementations. A
      symptom of a problematic cooperative close is that an unusually high feed
      is paid for the channel closure.
    </p>
    <p>
      Channel closures are probably the biggest pain point on the Lightning
      Network right now, and a lot of the problems happen when channels are
      "force-closed."
    </p>
    <p>
      If your channel partner was not online at the time you initiated the
      close, then the channel has to be force-closed. The channel will go into a
      CLOSED state, and the funds will be recovered after a delay of at least
      144 blocks (about 24 hours), but sometimes up to 2016 blocks (about 2
      weeks) -- this depends on the policy that your channel peer has set for
      the channel.
    </p>

    <p>
      You can find detailed data about closed channels in the History menu, with
      this button:
    </p>
    <DocsButton
      ariaLabel="Closed Channels button"
      text="Closed Channels"
      leftSection={<IconCircleMinus />}
    />

    <p>Here’s an example of detailed data for a closed channel:</p>
    <Box mb="20px">
      <JsonView
        data={{
          alias: "Rizful Node LSP Testnet",
          capacity: 1000000,
          channel_point:
            "9581ab3e11ab20e937c5f105264d7e7ada40d4c060919b960046651c6c7cba2c:0",
          close_confirm_height: 3162005,
          close_payments: [],
          close_transaction_id:
            "0d5a5d994c744f9ddd0a861b362d293c99f1fd60c01acae5a9554e83cd9cc5b9",
          final_local_balance: 50000,
          is_cooperative_close: true,
          is_local_force_close: false,
          is_partner_closed: false,
          is_partner_initiated: true,
          is_remote_force_close: false,
          partner_public_key:
            "02ac514a099bcaf0f05e86da39cf5178c7762b791f49bd6555da77ab86561c4b73",
          scid: "3127120x2318x0",
          synthetic_status: "closed",
        }}
        shouldExpandNode={allExpanded}
        style={darkStyles}
      />
    </Box>

    <p>Here’s an explanation of the data fields:</p>
    <ul>
      <li>
        <b>alias:</b> The alias of the remote node (your channel partner) for
        this closed channel, in this case, "Rizful Node LSP Testnet".
      </li>
      <li>
        <b>capacity:</b> The total capacity of the channel, in satoshis. This
        channel had a capacity of 1,000,000 satoshis.
      </li>
      <li>
        <b>channel_point:</b> A unique identifier for the channel, combining the
        transaction ID that created the channel and the output index (vout).
      </li>
      <li>
        <b>close_confirm_height:</b> The block height at which the channel was
        confirmed as closed, in this case, block 3,162,005.
      </li>
      <li>
        <b>close_payments:</b> Any payments made as part of the channel close
        process. This example shows an empty array, meaning no specific payments
        were included.
      </li>
      <li>
        <b>close_transaction_id:</b> The transaction ID of the closing
        transaction that settled the channel on-chain.{" "}
        <strong>
          Look up this transaction ID on a block explorer like mempool.space if
          you want to see when the transaction confirmed.
        </strong>
      </li>
      <li>
        <b>final_local_balance:</b> The amount of funds returned to your node
        after the channel closure. Here, it was 50,000 satoshis.
      </li>
      <li>
        <b>is_cooperative_close:</b> Indicates whether the channel was closed
        cooperatively by both parties. Cooperative closures are faster, cheaper,
        and in every way better, but are only possible when both nodes are
        online at the time of the closure.
      </li>
      <li>
        <b>is_local_force_close:</b> Indicates whether your node force-closed
        the channel. It’s false here, meaning this was not a unilateral closure.
      </li>
      <li>
        <b>is_partner_closed:</b> Indicates whether the remote partner initiated
        the closure. This is false, meaning the closure was not initiated by the
        partner.
      </li>
      <li>
        <b>is_partner_initiated:</b> Indicates whether the channel was
        originally opened by the remote partner. It’s true here, meaning the
        partner initiated the channel opening.
      </li>
    </ul>
  </TypographyStylesProvider>
);
